import React, { useEffect, useState } from 'react';
import BlogFigureControls from './BlogFigureControls';

// Progressive context-loading comparison used by the Harness article.

const STEPS = [
  {
    label: 'Session start',
    description: 'MCP loads the entire tool catalog before the task begins; the CLI only exposes a command name.',
    cliTokens: '~0 tokens',
    cliMeter: '0.5%',
  },
  {
    label: 'Discover',
    description: 'The CLI lists action names, then follows the one branch relevant to the task.',
    cliTokens: '~0.8k tokens',
    cliMeter: '2.9%',
  },
  {
    label: 'Read docs',
    description: 'Only the documentation for asset import enters context.',
    cliTokens: '~1.2k tokens',
    cliMeter: '4%',
  },
  {
    label: 'Act',
    description: 'The agent runs one command; the rest of the MCP catalog was loaded but never used.',
    cliTokens: '~1.5k tokens',
    cliMeter: '4.6%',
  },
];

const SCHEMA_BLOCKS = Array.from({ length: 120 }, (_, index) => index);
const USED_SCHEMA_INDEX = 41;

interface TreeRowProps {
  branch?: string;
  depth?: number;
  label: string;
  detail?: string;
  visible?: boolean;
  active?: boolean;
}

const TreeRow: React.FC<TreeRowProps> = ({
  branch = '',
  depth = 0,
  label,
  detail,
  visible = true,
  active = false,
}) => (
  <div
    className={`interface-tree__row${visible ? ' is-visible' : ''}${active ? ' is-active' : ''}`}
    style={{ paddingLeft: depth * 24 }}
    aria-hidden={!visible}
  >
    <span className="interface-tree__branch" aria-hidden="true">{branch}</span>
    <span className="interface-tree__node">
      <strong>{label}</strong>
      {detail && <small>{detail}</small>}
    </span>
  </div>
);

interface ContextMeterProps {
  width: string;
  label: string;
  tone: 'mcp' | 'cli';
}

const ContextMeter: React.FC<ContextMeterProps> = ({ width, label, tone }) => (
  <div className="interface-meter">
    <div className="interface-meter__label">
      <span>Context loaded</span>
      <strong>{label}</strong>
    </div>
    <div className="interface-meter__track">
      <div className={`interface-meter__fill interface-meter__fill--${tone}`} style={{ width }} />
    </div>
  </div>
);

const McpVsCliFigure: React.FC = () => {
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;

    const interval = window.setInterval(() => {
      setStep((current) => {
        if (current === STEPS.length - 1) {
          setRunning(false);
          return current;
        }
        return current + 1;
      });
    }, 2200);

    return () => window.clearInterval(interval);
  }, [running]);

  const togglePlayback = () => {
    if (running) {
      setRunning(false);
      return;
    }

    if (step === STEPS.length - 1) {
      setStep(0);
    }
    setRunning(true);
  };

  const currentStep = STEPS[step];

  return (
    <figure className="interface-figure">
      <div className="interface-figure__frame">
        <div className="interface-figure__viewport">
          <div className="interface-figure__comparison">
            <section className="interface-panel" aria-label="Illustrative MCP context loaded at session start">
              <header className="interface-panel__header">
                <div>
                  <strong>MCP</strong>
                  <span>entire tool catalog loaded up front</span>
                </div>
                <b>~32k est.</b>
              </header>

              <div className="schema-dump">
                <div className="schema-dump__header">
                  <span>turn 0 · context payload</span>
                  <span>120 schemas</span>
                </div>
                <div className="schema-dump__grid" aria-label="120 illustrative complete tool schemas">
                  {SCHEMA_BLOCKS.map((schema) => (
                    <span
                      key={schema}
                      className={step === 3 && schema === USED_SCHEMA_INDEX ? 'is-used' : ''}
                      title={step === 3 && schema === USED_SCHEMA_INDEX ? 'asset.import — used' : `illustrative tool schema ${schema + 1}`}
                    />
                  ))}
                </div>
                <div className="schema-dump__note">
                  {step === 3
                    ? '1 schema used · 119 were not needed'
                    : 'names, descriptions, and input schemas'}
                </div>
              </div>

              <ContextMeter width="100%" label="120 schemas · ~32k est." tone="mcp" />
            </section>

            <section className="interface-panel" aria-label="Illustrative CLI progressive command discovery">
              <header className="interface-panel__header">
                <div>
                  <strong>CLI</strong>
                  <span>one path, discovered on demand</span>
                </div>
                <b>{currentStep.cliTokens}</b>
              </header>

              <div className="interface-tree" role="tree" aria-label="Illustrative CLI command discovery tree">
                <TreeRow label="creative-app" detail="available on PATH" active={step === 0} />
                <TreeRow branch="├─" depth={1} label="actions" detail="list names" visible={step >= 1} active={step === 1} />
                <TreeRow branch="└─" depth={1} label="asset" visible={step >= 1} />
                <TreeRow branch="└─" depth={2} label="import" detail="selected action" visible={step >= 1} />
                <TreeRow branch="├─" depth={3} label="--help" detail="load docs" visible={step >= 2} active={step === 2} />
                <TreeRow
                  branch="└─"
                  depth={3}
                  label="project.edit clip.mp4"
                  detail="run"
                  visible={step >= 3}
                  active={step === 3}
                />
              </div>

              <ContextMeter width={currentStep.cliMeter} label={`${currentStep.cliTokens} · illustrative`} tone="cli" />
            </section>
          </div>
        </div>

        <div className="interface-figure__step" aria-live="polite">
          <strong>{step + 1}. {currentStep.label}</strong>
          <span> — {currentStep.description}</span>
        </div>

        <div className="interface-figure__progress">{step + 1} / {STEPS.length}</div>

        <BlogFigureControls
          className="interface-figure__controls"
          playing={running}
          onPrevious={() => setStep((current) => Math.max(0, current - 1))}
          onToggle={togglePlayback}
          onNext={() => setStep((current) => Math.min(STEPS.length - 1, current + 1))}
          previousDisabled={step === 0}
          nextDisabled={step === STEPS.length - 1}
          label="Interface comparison playback controls"
        />
      </div>

      <figcaption className="interface-figure__caption">
        Illustrative comparison: MCP front-loads a complete tool catalog, while a CLI reveals only the command path the agent needs.
      </figcaption>
    </figure>
  );
};

export default McpVsCliFigure;
